// ── 방문빈도 관리 ⚙️ 설정 — 지역 색상 기준 ──────────────────────────────────
// 2026-09-17 사용자 요청으로 동적 관리 > 영업 운영에서 **이 화면으로 옮겨왔다**(연동 아니라 이전).
//   동적 관리 쪽에서는 지웠다. 저장 값은 예전과 같은 자리(app_settings.region_color_rules)라
//   지도 색은 그대로 그려진다.
import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { cn } from '../../../lib/utils';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';

// 2026-09-22 사용자 요청: 낱말을 쉬운 말로 — '경계'→'기간 기준', '격상 기준'→'방문 횟수',
//   오른쪽 문구도 '주황 격상' 대신 '주황으로 변경'.
const ROWS = [
  { color: '#ef4444', label: '🔴 빨강', dayKey: 'day1', visitKey: null, visitLabel: null, desc: '설정 기간 이내 방문한 지역' },
  { color: '#f97316', label: '🟠 주황', dayKey: 'day2', visitKey: 'visit2', visitLabel: '번 넘게 갔으면 빨강으로 변경', desc: null },
  { color: '#eab308', label: '🟡 노랑', dayKey: 'day3', visitKey: 'visit3', visitLabel: '번 넘게 갔으면 주황으로 변경', desc: null },
  { color: '#22c55e', label: '🟢 초록', dayKey: 'day4', visitKey: 'visit4', visitLabel: '번 넘게 갔으면 노랑으로 변경', desc: null },
  { color: '#3b82f6', label: '🔵 파랑', dayKey: null, visitKey: 'visitMonth', visitLabel: '번 넘게 갔으면 초록으로 변경', desc: '지정한 기간을 모두 초과한 지역' },
];

const numberInput = 'w-14 border border-[var(--border)] rounded-md px-1.5 py-[3px] text-[11px] text-center bg-[var(--bg-card)] text-[var(--text)] outline-none';

// rulesUrl(2026-10-29): 색상 기준 주소 — 기본은 보는 사람 테넌트 것, 통합 메뉴는 그 메뉴 테넌트 것(/api/admin/integrated-menus/:id/region-color-rules)
export function RegionColorSettingsModal({ open, onClose, onSaved, rulesUrl = '/api/admin/settings/region-color-rules' }) {
  const { showToast } = useToast();
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    const r = await apiClient.get(rulesUrl);
    setLoading(false);
    if (!r.ok) { showToast('색상 기준을 불러오지 못했습니다.', 'error'); return; }
    setRules(r.data?.data || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesUrl]);

  useEffect(() => { if (open) fetchRules(); }, [open, fetchRules]);

  function change(key, value) {
    setRules((prev) => ({ ...prev, [key]: value === '' ? '' : Number(value) }));
  }

  async function save() {
    setSaving(true);
    const r = await apiClient.put(rulesUrl, rules);
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }
    showToast('색상 기준을 저장했습니다.', 'success');
    onSaved?.();
    onClose?.();
  }

  return (
    <Modal open={open} onClose={onClose} title="지역 색상 기준" icon="🎨" width="min(560px, calc(100vw - 48px))">
      <div className="flex flex-col gap-2.5">
        <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
          방문빈도 지도에 찍히는 동그라미 색을 정하는 기준입니다.<br />
          <strong>기간 기준</strong>(마지막 방문 후 며칠 지났나)으로 색을 정하고,
          그 사이 <strong>방문 횟수</strong>가 많으면 더 붉은 쪽으로 바뀝니다.
        </div>
        {loading && <div className="py-3 text-xs text-[var(--text-muted)]">불러오는 중...</div>}
        {!loading && ROWS.map(({ color, label, dayKey, visitKey, visitLabel, desc }) => (
          <div key={label} className="p-[9px_10px] rounded-lg bg-[var(--bg-subtle)] border border-[var(--border)] flex flex-col gap-1.5">
            <div className="flex items-center gap-[7px]">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{label}</span>
              {desc && <span className="text-[10px] text-[var(--text-muted)]">{desc}</span>}
            </div>
            {dayKey && (
              <div className="flex items-center gap-1.5 pl-[17px]">
                <span className="text-[11px] text-[var(--text-muted)] w-[60px] flex-shrink-0">기간 기준</span>
                <input type="number" min="1" max="365" value={rules[dayKey] ?? ''} onChange={(e) => change(dayKey, e.target.value)} className={numberInput} />
                {/* 계산이 `경과일 < 기준일` 이라 '이하'가 아니라 '미만'이다.
                    기준이 7이면 7일째는 이미 다음 색. 문구가 하루 어긋나 있어 바로잡았다(2026-09-22). */}
                <span className="text-[11px] text-[var(--text-muted)]">일 미만</span>
              </div>
            )}
            {visitKey && (
              <div className="flex items-center gap-1.5 pl-[17px]">
                <span className="text-[11px] text-[var(--text-muted)] w-[60px] flex-shrink-0">방문 횟수</span>
                <input type="number" min="1" max="365" value={rules[visitKey] ?? ''} onChange={(e) => change(visitKey, e.target.value)} className={numberInput} />
                <span className="text-[11px] text-[var(--text-muted)]">{visitLabel}</span>
              </div>
            )}
          </div>
        ))}
        <button onClick={save} disabled={saving || loading}
          className={cn('self-end px-5 py-[7px] rounded-lg border-none text-xs font-bold transition-colors',
            saving ? 'bg-[var(--bg-subtle)] text-[var(--text-muted)] cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-accent-600 dark:hover:bg-accent-500 cursor-pointer')}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </Modal>
  );
}
